/**
 * Server discovery feed (Phase 3) — primary visibility + freemium caps live in
 * Postgres RPC `get_discovery_feed`. Clients must not re-decide staffelung for feed rows.
 */
import { supabase } from "@/lib/supabase";

export type DiscoveryTeaserRow = {
  id: string;
  category: string;
  teaser_image_url: string | null;
  is_top_pick: boolean;
  /** Present on event teasers (cap overflow + beyond-horizon Concerts). */
  starts_at?: string | null;
};

export type DiscoveryFeedSection<TFull> = {
  full: TFull[];
  teasers: DiscoveryTeaserRow[];
  visible_count: number;
  total_count: number;
};

export type DiscoveryFeedResponse = {
  tier: string;
  city_key: string;
  as_of: string;
  seed_date: string;
  venues: DiscoveryFeedSection<Record<string, unknown>>;
  live_events: DiscoveryFeedSection<Record<string, unknown>>;
};

const TEASER_REQUIRED_KEYS = new Set(["id", "category", "teaser_image_url", "is_top_pick"]);
/** Optional on event teasers — never allow full-payload leaks beyond this allowlist. */
const TEASER_OPTIONAL_KEYS = new Set(["starts_at"]);

/** Assert teaser rows never leak full-payload fields (defense in depth on client). */
export function assertDiscoveryTeaserShape(row: Record<string, unknown>): DiscoveryTeaserRow {
  const keys = Object.keys(row);
  const unexpected = keys.filter(
    (k) => !TEASER_REQUIRED_KEYS.has(k) && !TEASER_OPTIONAL_KEYS.has(k),
  );
  if (unexpected.length > 0) {
    throw new Error(`Discovery teaser leaked keys: ${unexpected.join(", ")}`);
  }
  for (const required of TEASER_REQUIRED_KEYS) {
    if (!(required in row)) {
      throw new Error(`Discovery teaser missing key: ${required}`);
    }
  }
  return {
    id: String(row.id),
    category: String(row.category ?? ""),
    teaser_image_url: row.teaser_image_url == null ? null : String(row.teaser_image_url),
    is_top_pick: Boolean(row.is_top_pick),
    starts_at: row.starts_at == null ? null : String(row.starts_at),
  };
}

function normalizeSection(raw: unknown): DiscoveryFeedSection<Record<string, unknown>> {
  const section = (raw ?? {}) as Record<string, unknown>;
  const full = Array.isArray(section.full) ? (section.full as Record<string, unknown>[]) : [];
  const teasersRaw = Array.isArray(section.teasers)
    ? (section.teasers as Record<string, unknown>[])
    : [];
  return {
    full,
    teasers: teasersRaw.map(assertDiscoveryTeaserShape),
    visible_count: Number(section.visible_count ?? full.length),
    total_count: Number(section.total_count ?? full.length + teasersRaw.length),
  };
}

export async function fetchDiscoveryFeed(
  cityKey: string,
  options?: { asOf?: string | null; tierOverride?: string | null },
): Promise<DiscoveryFeedResponse> {
  // RPC matches target_cities.city_key (lowercase slug), not display names.
  const normalizedCity = cityKey.trim().toLowerCase();
  const { data, error } = await supabase.rpc("get_discovery_feed", {
    p_city_key: normalizedCity,
    p_as_of: options?.asOf ?? new Date().toISOString(),
    p_tier_override: options?.tierOverride ?? null,
  });
  if (error) throw error;

  const raw = (data ?? {}) as Record<string, unknown>;
  return {
    tier: String(raw.tier ?? ""),
    city_key: String(raw.city_key ?? cityKey),
    as_of: String(raw.as_of ?? ""),
    seed_date: String(raw.seed_date ?? ""),
    venues: normalizeSection(raw.venues),
    live_events: normalizeSection(raw.live_events),
  };
}
